import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import _ from 'lodash';
import cn from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { getDigitalFormatTypeString } from '@bibliocommons/utils-format';
import { bindAll } from '@bibliocommons/utils-react';
import Loader from 'app/components/shared/Loader/Loader';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import DigitalDurations from '../../digital-durations';
import '../styles/DigitalFormats.scss';

export class DigitalFormats extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selected: null
    };
    bindAll(this);
  }

  componentDidMount() {
    if (!this.props.formats) {
      this.props.fetchDigitalFormats(this.props.metadataId);
    }
  }

  getDigitalFormatDescription() {
    const { formats, selectable, intl, provider, isAvailable } = this.props;
    const format = formats.first().get('name');
    const formatType = getDigitalFormatTypeString(format);
    if (selectable) {
      return <FormattedMessage id={`please_pick_a_format_${formatType}`} />;
    }

    const renderMessageWithFallback = (id, messageProps, fallback) => {
      if (intl && intl.messages && intl.messages[id]) {
        return <FormattedMessage id={id} {...messageProps} />;
      }
      return fallback;
    };

    const innerMessage = renderMessageWithFallback(
      `${formatType}_available_now`,
      {
        values: {
          help_link: (
            <a
              href={intl.formatMessage({
                id: `${formatType}_help_url`
              })}
            >
              <span aria-hidden>
                <FormattedMessage id="learn_more" />
              </span>
              <ScreenReaderMessage>
                <FormattedMessage id={`ada_${formatType}_help_text`} />
              </ScreenReaderMessage>
            </a>
          )
        }
      },
      <FormattedMessage id={isAvailable ? 'available_formats_now' : 'available_formats'} />
    );

    return renderMessageWithFallback(`digital_content_${provider.toLowerCase()}_desc`, {}, innerMessage);
  }

  /* istanbul ignore next */
  /* eslint-disable global-require */
  static getFormatImage(format) {
    switch (format.get('name').toLowerCase()) {
      case 'acoustik':
        return require('@bibliocommons/images/digital-formats/acoustik.png');
      case 'adobe_epub_ebook':
        return require('@bibliocommons/images/digital-formats/adobe_epub_ebook.png');
      case 'adobe_pdf_ebook':
        return require('@bibliocommons/images/digital-formats/adobe_pdf_ebook.png');
      case 'biblio_ereader':
        return require('@bibliocommons/images/digital-formats/biblio_ereader.png');
      case 'blio':
        return require('@bibliocommons/images/digital-formats/blio.png');
      case 'kindle':
        return require('@bibliocommons/images/digital-formats/kindle.png');
      case 'mmm_ebook':
        return require('@bibliocommons/images/digital-formats/mmm_ebook.png');
      case 'music':
        return require('@bibliocommons/images/digital-formats/music.png');
      case 'open_epub_ebook':
        return require('@bibliocommons/images/digital-formats/open_epub_ebook.png');
      case 'open_pdf_ebook':
        return require('@bibliocommons/images/digital-formats/open_pdf_ebook.png');
      case 'overdrive_listen':
        return require('@bibliocommons/images/digital-formats/libby.png');
      case 'overdrive_mp3_audiobook':
        return require('@bibliocommons/images/digital-formats/overdrive_mp3_audiobook.png');
      case 'overdrive_read':
        return require('@bibliocommons/images/digital-formats/libby.png');
      case 'overdrive_wma_audiobook':
        return require('@bibliocommons/images/digital-formats/overdrive_wma_audiobook.png');
      case 'streaming_video':
        return require('@bibliocommons/images/digital-formats/streaming_video.png');
      case 'video':
        return require('@bibliocommons/images/digital-formats/video.png');
      default:
        return null;
    }
  }
  /* eslint-enable global-require */

  handleSelect(format) {
    this.setState({
      selected: format
    });
    this.props.onSelectFormat(format);
  }

  renderDurations() {
    if (!this.state.selected || !this.props.selectable) {
      return null;
    }
    const durations = this.state.selected.get('durations');
    if (durations && durations.size > 1) {
      return <DigitalDurations durations={durations} handleSelect={this.props.handleDurationSelect} />;
    }

    if (durations && durations.size === 1) {
      this.props.handleDurationSelect(durations.first());
    }

    return null;
  }

  renderFormat(format) {
    let radioButton = null;
    const formatLabel = format.get('additionalInfo') || (
      <FormattedHTMLMessage id={`digiformat_${format.get('name').toLowerCase()}`} />
    );
    const image = DigitalFormats.getFormatImage(format);
    const formatSelectable =
      this.props.selectable && (format.get('hasDownloadLink') || !format.get('durations').isEmpty());
    const id = `format-${format.get('name')}`;
    const classNames = cn('format-item', `format-${_.kebabCase(format.get('name'))}`, {
      selectable: formatSelectable,
      selected: this.state.selected === format
    });
    if (formatSelectable) {
      radioButton = (
        <input
          type="radio"
          name="format-select"
          id={id}
          className="format-radio"
          onChange={() => this.handleSelect(format)}
          required
        />
      );
    }
    return (
      <label key={format.get('name')} htmlFor={id} className={classNames}>
        {radioButton}
        <div className="format-label">
          <span className="format-label-name">{formatLabel}</span>
          {image && <img src={image} alt="" className="format-logo" loading="lazy" />}
        </div>
      </label>
    );
  }

  render() {
    if (!this.props.formats) {
      return <Loader />;
    }
    if (this.props.formats.count() === 0) {
      return (
        <h3>
          <FormattedMessage id="digital_format_not_available" />
        </h3>
      );
    }
    return (
      <div className="cp-digital-formats">
        <span>{this.getDigitalFormatDescription()}</span>
        <div className="format-group">{this.props.formats.map(this.renderFormat)}</div>
        {this.renderDurations()}
      </div>
    );
  }
}

DigitalFormats.displayName = 'DigitalFormats';

DigitalFormats.propTypes = {
  formats: ImmutablePropTypes.listOf(
    ImmutablePropTypes.contains({
      name: PropTypes.string,
      hasDownloadLink: PropTypes.bool
    })
  ),
  isAvailable: PropTypes.bool,
  provider: PropTypes.string,
  selectable: PropTypes.bool,
  onSelectFormat: PropTypes.func,

  handleDurationSelect: PropTypes.func,
  fetchDigitalFormats: PropTypes.func.isRequired,
  metadataId: PropTypes.string.isRequired
};

export default injectIntl(DigitalFormats);
